import { ColDef, ValueGetterParams } from 'ag-grid-community';
import Decimal from 'decimal.js';
import { DateTime } from 'luxon';
import CheckboxCellRender from '../components/BaseTable/partials/CheckBoxCell/CheckBoxCellRender';
import Formatter from './formatters';
import { Document } from '../services/http/documents.api';
import { BaseTag } from '../services/http/tag.api';
import TagFormat from './tagFormat';
import { ElectronicFlgLabels, PaperFlgLabels } from './document.utils';

export default class AGUtils {
  /**
   * オートインクリメントを行うためのカラム設定を返します。
   *
   * @param field - カラムに対応するデータのフィールド名
   * @param headerName - カラムの見出し名
   * @param startIndex - 開始インデックス
   * @param sortable - ソート可能かどうか (デフォルトはtrue)
   * @returns - 生成されたカラムの設定
   */
  static colAutoIncrement(field: string, headerName = 'No', startIndex = 0): ColDef {
    return {
      field,
      headerName,
      resizable: true,
      valueGetter: (params) => String((params.node?.childIndex || 0) + 1 + startIndex),
      width: 80,
    };
  }

  /**
   * デフォルト設定のカラムを返します。
   *
   * @param field - フィールド名
   * @param headerName - ヘッダー名
   * @param width - 列の幅 (デフォルトは150)
   * @param sortable - ソート可能かどうか (デフォルトはtrue)
   * @param isHighlight - ハイライト対象かどうか (デフォルトはfalse)
   * @param additionalOptions - 追加のオプション
   */
  static colDefault(field: string, headerName: string, width = 150, sortable = true, isHighlight = false, additionalOptions: ColDef = {}): ColDef {
    return {
      field,
      headerName,
      resizable: true,
      sortable,
      width,
      cellClass: isHighlight ? 'textFormat highlightTarget' : 'textFormat',
      ...additionalOptions,
    };
  }

  /**
   * ユーザー名に専用のカラムの設定を返します。
   *
   * @param field - フィールド名
   * @param headerName - ヘッダー名
   * @param sortable - ソート可能かどうか (デフォルトはtrue)
   * @param additionalOptions - 追加のオプション
   * @returns - カラム定義
   */
  static colUsername(field: string, headerName: string, sortable = true, additionalOptions: ColDef = {}): ColDef {
    return {
      field,
      headerName,
      resizable: true,
      sortable,
      width: 150,
      cellClass: 'textFormat',
      ...additionalOptions,
    };
  }

  /**
   * Emailアドレス専用のカラムの設定を返します。
   *
   * @param field - フィールド名
   * @param headerName - ヘッダー名
   * @returns - カラム定義
   */
  static colEmail(field: string, headerName: string): ColDef {
    return {
      field,
      headerName,
      resizable: true,
      sortable: true,
      width: 200,
      cellClass: 'textFormat',
    };
  }

  /**
   * 日付専用のカラムの設定を返します。
   *
   * @param field - フィールド名
   * @param headerName - ヘッダー名
   * @param sortable - ソート可能かどうか (デフォルトはtrue)
   * @param additionalOptions - 追加のオプション
   * @param format - 日付のフォーマット (デフォルトはdefaultDateTimeFormat)
   * @returns カラム定義
   */
  static colDate(field: string, headerName: string, sortable = true, additionalOptions: ColDef = {}, format?: string): ColDef {
    const dateFormat = format || Formatter.defaultDateTimeFormat;
    return {
      field,
      headerName,
      resizable: true,
      sortable,
      width: 180,
      cellClass: 'textFormat',
      valueGetter: ({ data }) => (typeof data[field] === 'string' ? Formatter.fromStringToFormattedString(data[field], dateFormat) : Formatter.toDisplayDate(data[field], dateFormat)),
      ...additionalOptions,
    };
  }

  /**
   * ファイルサイズ専用のカラムの設定を返します。
   *
   * @param field - フィールド名
   * @param headerName - ヘッダー名
   * @param sortable - ソート可能かどうか (デフォルトはtrue)
   * @param additionalOptions - 追加のオプション
   * @returns - カラム定義
   */
  static colFileSize(field: string, headerName: string, sortable = true, additionalOptions: ColDef = {}): ColDef {
    return {
      field,
      headerName,
      resizable: true,
      sortable,
      width: 100,
      cellClass: 'textFormat',
      comparator: (valueA, valueB, nodeA, nodeB) => {
        const fileSizeA = nodeA.data[field];
        const fileSizeB = nodeB.data[field];
        if (fileSizeA === fileSizeB) return 0;
        return (fileSizeA > fileSizeB) ? 1 : -1;
      },
      valueGetter: ({ data }) => Formatter.byteSizeDescription(data[field]),
      ...additionalOptions,
    };
  }

  /**
   * チェックボックス専用のカラムの設定を返します。
   *
   * @param field - フィールド名
   * @param headerName - ヘッダー名
   * @param width - 列の幅 (デフォルトは150)
   * @returns - カラム定義
   */
  static colCheckboxReadOnly(field: string, headerName: string, width = 150): ColDef {
    return {
      field,
      headerName,
      resizable: true,
      sortable: true,
      width,
      cellRenderer: CheckboxCellRender,
      cellRendererParams: {
        readOnly: true,
      },
    };
  }

  /**
   * 契約種別専用のカラムの設定を返します。
   *
   * @param field - フィールド名
   * @param headerName - ヘッダー名
   * @param width - 列の幅 (デフォルトは150)
   * @param sortable - ソート可能かどうか (デフォルトはtrue)
   * @param additionalOptions - 追加のオプション
   * @returns カラム定義
   */
  static colElectronicFlg(field: string, headerName: string, width = 150, sortable = true, additionalOptions: ColDef = {}): ColDef {
    return {
      field,
      headerName,
      resizable: true,
      sortable,
      width,
      valueGetter: (params: ValueGetterParams) => {
        const value: keyof typeof ElectronicFlgLabels = params.data?.[field];
        if (value == null) return null;
        return ElectronicFlgLabels[value];
      },
      comparator: (valueA, valueB, nodeA, nodeB) => {
        const a = nodeA.data[field];
        const b = nodeB.data[field];
        if (a === b) return 0;
        return (a < b) ? 1 : -1;
      },
      ...additionalOptions,
    };
  }

  /**
   * 書面有無専用のカラムの設定を返します。
   *
   * @param field - フィールド名
   * @param headerName - ヘッダー名
   * @param width - 列の幅 (デフォルトは150)
   * @param sortable - ソート可能かどうか (デフォルトはtrue)
   * @param additionalOptions - 追加のオプション
   * @returns カラム定義
   */
  static colPaperFlg(field: string, headerName: string, width = 150, sortable = true, additionalOptions: ColDef = {}): ColDef {
    return {
      field,
      headerName,
      resizable: true,
      sortable,
      width,
      valueGetter: (params: ValueGetterParams) => {
        const value: keyof typeof PaperFlgLabels = params.data?.[field];
        if (value == null) return null;
        return PaperFlgLabels[value];
      },
      comparator: (valueA, valueB, nodeA, nodeB) => {
        const a = nodeA.data[field];
        const b = nodeB.data[field];
        if (a === b) return 0;
        return (a > b) ? 1 : -1;
      },
      ...additionalOptions,
    };
  }

  /**
   * エクスポートファイル名を生成します。
   * 形式: cloudcabinet_{画面名}_yyyyMMddHHmmss
   * 例: cloudcabinet_文書一覧_20260622130743
   *
   * @param featureName - 画面名（例: 文書一覧）
   * @returns - 生成したファイル名（拡張子は ag-grid 側で付与される）
   */
  static exportFileName(featureName: string): string {
    return `cloudcabinet_${featureName}_${DateTime.now().toFormat('yyyyMMddHHmmss')}`;
  }

  /**
   * 数値処理
   *
   * @param n - 文字列としての数値
   * @returns - 数値
   */
  static processNumber(n: string) {
    return n ? Number(n) : null;
  }

  /**
   * タグ処理
   *
   * @param tagName - タグ名
   * @param tag - APIからのタグ
   * @returns - valueGetter
   */
  static getTagValueGetter = (tagName: string, tag: BaseTag | null) => ({ data }: ValueGetterParams<Document>) => {
    if (!data || !data.tagList) return '';
    const docTag = data.tagList.find((t: { label: string, value: string }) => t.label === tagName);

    if (docTag) {
      return docTag.value;
    }
    return AGUtils.processNumber('');
  };

  static customTagComparator = (tag: BaseTag | null): ColDef['comparator'] => {
    if (tag?.format === TagFormat.NUMBER) {
      return (valueA: string, valueB: string): number => {
        const isANull = valueA === null || valueA === undefined || valueA === '';
        const isBNull = valueB === null || valueB === undefined || valueB === '';

        if (isANull && isBNull) return 0;
        if (isANull) return -1;
        if (isBNull) return 1;

        try {
          const a = new Decimal(valueA);
          const b = new Decimal(valueB);
          return a.comparedTo(b);
        } catch (e) {
          return 0;
        }
      };
    }

    return undefined;
  };
}
